import { Alert, Badge, Button, Spinner } from "@dms/ui";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { useResource } from "../hooks/useResource";
import { notificationLink, notificationText, notificationsApi, type Notification } from "../lib/notificationsApi";

function formatDateTime(iso: string | null): string {
  if (iso === null) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("uk-UA", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Popover хедера (12-screen-spec §11): 10 останніх + «Прочитати все» + лінк на повну
// сторінку. Монтується лише коли відкрито → свіжий список щоразу. Позиціюється під
// дзвоником (anchorEl) як ClearFlow-панель (без MUI).
export function NotificationsPopover({
  anchorEl,
  onClose,
  onChanged,
}: {
  anchorEl: HTMLElement;
  onClose: () => void;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useResource(() => notificationsApi.list({ perPage: 10 }), []);
  const [busy, setBusy] = useState(false);

  const markAll = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await notificationsApi.markAllRead();
      reload();
      onChanged();
    } catch {
      // fail-soft: лишаємо список як є (повна сторінка показує чесні помилки).
    } finally {
      setBusy(false);
    }
  };

  const rect = anchorEl.getBoundingClientRect();

  const renderItem = (notification: Notification) => {
    const link = notificationLink(notification);
    const unread = notification.read_at === null;
    const inner = (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {unread && <Badge tone="brand" dot>Нове</Badge>}
          <span style={{ fontSize: "var(--fs-body-sm)", fontWeight: unread ? 600 : 400, color: "var(--color-text-primary)" }}>
            {notification.presentation?.title ?? notificationText(notification)}
          </span>
        </div>
        {notification.presentation?.description && (
          <div style={{ fontSize: "var(--fs-meta)", whiteSpace: "pre-line", color: "var(--color-text-secondary)", marginTop: 3 }}>
            {notification.presentation.description}
          </div>
        )}
        <div style={{ fontSize: "var(--fs-meta)", color: "var(--color-text-secondary)", marginTop: 2 }}>
          {notification.presentation?.occurred_at_label ?? formatDateTime(notification.created_at)}
        </div>
      </>
    );
    const rowStyle = { display: "block", padding: "10px 16px", borderBottom: "1px solid var(--color-border-subtle)", textDecoration: "none", color: "inherit" } as const;
    const open = async () => {
      if (unread) {
        try {
          await notificationsApi.markRead(notification.id);
          onChanged();
        } catch {
          // fail-soft: direct link лишається доступним.
        }
      }
      onClose();
      if (link) navigate(link);
    };
    return link !== null ? (
      <button type="button" key={notification.id} onClick={() => void open()} style={{ ...rowStyle, width: "100%", textAlign: "left", background: "transparent", border: 0, cursor: "pointer" }}>
        {inner}
      </button>
    ) : (
      <div key={notification.id} style={rowStyle}>
        {inner}
      </div>
    );
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 499 }} />
      <div
        className="notifications-popover"
        style={{
          position: "fixed",
          top: rect.bottom + 6,
          right: Math.max(8, window.innerWidth - rect.right),
          width: 360,
          maxWidth: "90vw",
          maxHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          background: "var(--neutral-0)",
          border: "1px solid var(--color-border-default)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-popover)",
          zIndex: 500,
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid var(--color-border-subtle)" }}>
          <span style={{ fontSize: "var(--fs-subtitle)", fontWeight: 600 }}>Сповіщення</span>
          <Button variant="ghost" size="sm" onClick={() => void markAll()} disabled={busy || (data?.unreadCount ?? 0) === 0}>
            Прочитати все
          </Button>
        </div>

        <div style={{ overflowY: "auto" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }} role="status">
              <Spinner size={24} />
            </div>
          ) : error !== null ? (
            <div style={{ padding: 16 }}>
              <Alert tone="error">Не вдалося завантажити сповіщення.</Alert>
            </div>
          ) : data === null || data.notifications.length === 0 ? (
            <p style={{ padding: 16, margin: 0, fontSize: "var(--fs-body-sm)", color: "var(--color-text-secondary)" }}>
              Сповіщень немає.
            </p>
          ) : (
            data.notifications.map(renderItem)
          )}
        </div>

        <div style={{ padding: 10, textAlign: "center", borderTop: "1px solid var(--color-border-subtle)" }}>
          <RouterLink to="/notifications" onClick={onClose} style={{ color: "var(--color-text-link)", fontSize: "var(--fs-body-sm)" }}>
            Усі сповіщення
          </RouterLink>
        </div>
      </div>
    </>
  );
}
